// Agrupa una lista de archivos por su "stem" — el nombre base sin la
// extensión ni el sufijo numérico que la mayoría de herramientas le
// agregan cuando exportan/duplican imágenes.
//
// Patrones reconocidos (todos al final del stem, después de quitar
// extensión):
//   • remera_negra_1.png, remera_negra_2.png         → "remera_negra"
//   • remera_negra-1.png, remera_negra-2.png         → "remera_negra"
//   • remera_negra 1.png, remera_negra 2.png         → "remera_negra"
//   • remera_negra.1.png, remera_negra.2.png         → "remera_negra"
//   • remera_negra(1).png, remera_negra(2).png       → "remera_negra"
//   • remera_negra (1).png                            → "remera_negra"
//   • remera_negra copy.png, remera_negra copy 2.png  → "remera_negra"
//   • remera_negra copia.png, remera_negra copia 3   → "remera_negra"
//   • remera_negra_copy_2.png                         → "remera_negra"
//   • IMG_0001.jpg sin grupo se mantiene como "IMG_0001"
//   • Si después de stripear no queda nada usable, se cae al stem crudo.
//
// El agrupamiento es case-insensitive y colapsa separadores, así que
// `Remera Negra` y `remera_negra` caen en el mismo grupo.

export interface FileGroup<T = File> {
  /** Clave normalizada (lowercase + separadores colapsados) — útil para
   *  identificar el grupo de forma estable entre renders. */
  key: string;
  /** Nombre legible derivado del archivo (sin extensión ni sufijo de
   *  numeración). Se conserva el casing y los símbolos del primer
   *  archivo del grupo, pero los separadores se convierten en espacio
   *  para que la UI lo muestre bonito. */
  baseName: string;
  /** Elementos pertenecientes al grupo, en el orden en que se vieron. */
  files: T[];
}

/** Quita la extensión final del nombre de archivo. */
const stripExtension = (name: string): string => {
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return name;
  // Solo recortamos si lo que sigue al `.` parece una extensión (1-5 chars
  // alfanuméricos). Evita comer cosas como "v1.2".
  const ext = name.slice(dot + 1);
  if (/^[a-z0-9]{1,5}$/i.test(ext)) return name.slice(0, dot);
  return name;
};

/** Elimina sufijos de numeración o de "copia" al final del stem. Aplica
 *  los patrones repetidamente porque algunos archivos los encadenan
 *  ("remera copy 2", "remera_1_copy"). */
const stripTrailingSuffix = (stem: string): string => {
  let prev = "";
  let s = stem;
  while (prev !== s) {
    prev = s;
    // (N) o (N) con espacios opcionales delante
    s = s.replace(/[\s._-]*\(\s*\d+\s*\)\s*$/u, "");
    // copy / copia con número opcional, con separador opcional delante
    s = s.replace(/[\s._-]*(copy|copia)([\s._-]*\d+)?\s*$/iu, "");
    // separador + número al final (_, -, ., espacio)
    s = s.replace(/[\s._-]+\d+\s*$/u, "");
  }
  return s.trim();
};

/** Clave de agrupamiento: stripeada + lowercased + colapsando separadores
 *  para que "Remera Negra" y "remera_negra" caigan en el mismo grupo. */
const groupKey = (stripped: string): string => {
  return stripped
    .toLowerCase()
    .replace(/[\s._-]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

/** Versión amigable del nombre — preserva el casing original del primer
 *  archivo del grupo pero reemplaza separadores `_`/`-`/`.`/espacios
 *  múltiples por un único espacio para que se vea bien en UI. */
const prettifyBaseName = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return "Sin nombre";
  return trimmed
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

/** Devuelve el `baseName` que tendría un archivo si fuera el único de su
 *  grupo. Útil para casos donde no necesitás agrupar pero querés el mismo
 *  algoritmo de naming. */
export const baseNameFromFileName = (fileName: string): string => {
  const stem = stripExtension(fileName || "imagen");
  const stripped = stripTrailingSuffix(stem) || stem;
  return prettifyBaseName(stripped || stem);
};

/** Agrupa los items por stem stripeado. Cada item cae en exactamente
 *  un grupo; los grupos se devuelven en el orden de primer aparición.
 *  Se le pasa un `getName` para extraer el filename de cada item — así
 *  funciona tanto con `File[]` puros como con structs que envuelven
 *  archivos (e.g. BulkItem). */
export const groupItemsByStem = <T>(
  items: T[],
  getName: (item: T) => string,
): FileGroup<T>[] => {
  const groups = new Map<string, FileGroup<T>>();
  for (const item of items) {
    const rawName = getName(item) || "imagen";
    const stem = stripExtension(rawName);
    const stripped = stripTrailingSuffix(stem) || stem;
    const key = groupKey(stripped) || groupKey(stem) || rawName;
    const existing = groups.get(key);
    if (existing) {
      existing.files.push(item);
    } else {
      groups.set(key, {
        key,
        baseName: prettifyBaseName(stripped || stem),
        files: [item],
      });
    }
  }
  return Array.from(groups.values());
};

/** Atajo para agrupar archivos crudos. */
export const groupFilesByStem = (files: File[]): FileGroup<File>[] =>
  groupItemsByStem(files, (f) => f.name);
